import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, FileJson, FileText, ShieldOff, FileDown, Loader2, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

const PDF_SECTIONS = [
  { k: "profile",       l: "Profile"        },
  { k: "vitals",        l: "Vitals"         },
  { k: "medications",   l: "Medications"    },
  { k: "allergies",     l: "Allergies"      },
  { k: "vaccinations",  l: "Vaccinations"   },
  { k: "emergency",     l: "Emergency card" },
];

/* Privacy Center + Export/Delete UI — §51, §53, §54.
   PDF is now server-rendered (reportlab) with a selectable-section picker. */
export default function PrivacyView({ onBack, onVaultDeleted }) {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [picked, setPicked] = useState(() => new Set(PDF_SECTIONS.map((s) => s.k)));

  useEffect(() => {
    api.get("/vault/health/audit?limit=50").then((r) => setAudit(r.data.items || [])).catch(() => setAudit([]));
  }, []);

  const togglePick = (k) => {
    const next = new Set(picked);
    if (next.has(k)) next.delete(k); else next.add(k);
    setPicked(next);
  };

  const dl = async (url, filename) => {
    setBusy(true);
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`Exported ${filename}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    setBusy(false);
  };

  const dlPdf = () => {
    if (picked.size === 0) return toast.error("Pick at least one section");
    dl(`/vault/health/export.pdf?sections=${Array.from(picked).join(",")}`, "health-vault-summary.pdf");
  };

  const deleteVault = async () => {
    if (confirmText !== "DELETE MY HEALTH VAULT") {
      return toast.error("Type the confirmation phrase exactly to enable deletion");
    }
    if (!window.confirm("This will permanently delete all your Health Vault data and revoke every emergency share link. This cannot be undone. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.delete("/vault/health/all");
      toast.success(`Vault deleted (${data.vitals_removed} readings, ${data.medications_removed} medications, ${data.allergies_removed} allergies)`);
      onVaultDeleted && onVaultDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    setBusy(false);
  };

  return (
    <div data-testid="privacy-view">
      <button onClick={onBack} data-testid="privacy-back" className="text-sm text-muted-foreground inline-flex items-center gap-1 hover:text-primary mb-3">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-gradient-to-br from-slate-100 to-white border border-zinc-200 p-4 mb-5">
        <h1 className="font-heading font-900 text-2xl">Privacy &amp; Data</h1>
        <p className="text-xs text-muted-foreground mt-1">Your Health Vault is isolated from ads, analytics and search. You can export or delete it at any time.</p>
      </div>

      <div className="border border-zinc-200 bg-white p-4 mb-5">
        <h3 className="font-heading font-800 text-sm mb-3 inline-flex items-center gap-1.5"><FileDown className="w-4 h-4 text-emerald-600" /> Branded PDF summary</h3>
        <p className="text-xs text-muted-foreground mb-3">Pick the sections to include. Rendered server-side with proper page breaks and branding — nice for printing or sharing with a physician.</p>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {PDF_SECTIONS.map((s) => (
            <button key={s.k} onClick={() => togglePick(s.k)} data-testid={`pdf-section-${s.k}`}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-700 border ${picked.has(s.k) ? "bg-emerald-600 text-white border-emerald-600" : "border-zinc-300 text-zinc-700 hover:border-emerald-400"}`}>
              {picked.has(s.k) && <CheckCircle2 className="w-3 h-3" />} {s.l}
            </button>
          ))}
        </div>
        <button onClick={dlPdf} disabled={busy || picked.size === 0} data-testid="export-pdf"
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-700 disabled:opacity-50 hover:bg-emerald-700">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Download PDF
        </button>
      </div>

      <div className="border border-zinc-200 bg-white p-4 mb-5">
        <h3 className="font-heading font-800 text-sm mb-3">Raw data export</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button onClick={() => dl("/vault/health/export", "health-vault-export.json")} disabled={busy} data-testid="export-json" className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-zinc-300 text-sm font-700 hover:border-primary disabled:opacity-60"><FileJson className="w-4 h-4" /> JSON</button>
          <button onClick={() => dl("/vault/health/export.csv", "health-vault-export.csv")} disabled={busy} data-testid="export-csv" className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-zinc-300 text-sm font-700 hover:border-primary disabled:opacity-60"><FileText className="w-4 h-4" /> CSV</button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Exports contain everything you've stored. Save them somewhere safe — they are not encrypted after leaving the vault.</p>
      </div>

      <div className="border border-zinc-200 bg-white mb-5">
        <div className="p-3 border-b border-zinc-100 text-sm font-700">Recent activity</div>
        {audit === null ? <div className="p-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
          : audit.length === 0 ? <p className="p-4 text-sm text-muted-foreground">No activity recorded yet.</p>
          : audit.slice(0, 20).map((a, i) => (
            <div key={i} className="px-4 py-2 border-b border-zinc-100 last:border-b-0 text-xs flex items-center justify-between">
              <span className="text-zinc-800 font-mono">{a.event}</span>
              <span className="text-muted-foreground font-mono">{new Date(a.ts).toLocaleString()}</span>
            </div>
          ))}
      </div>

      <div className="border border-rose-300 bg-rose-50 p-4" data-testid="delete-vault-panel">
        <h3 className="font-heading font-800 text-sm flex items-center gap-1.5 text-rose-800"><ShieldOff className="w-4 h-4" /> Delete Health Vault</h3>
        <p className="text-xs text-rose-800 mt-2">Permanently deletes vitals, medications, allergies, emergency card and revokes every share link. This cannot be undone.</p>
        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} data-testid="delete-confirm-text"
          placeholder="Type: DELETE MY HEALTH VAULT" className="mt-3 w-full border border-rose-400 px-3 py-2 text-sm font-mono bg-white" />
        <button onClick={deleteVault} disabled={busy || confirmText !== "DELETE MY HEALTH VAULT"} data-testid="delete-vault-confirm"
          className="mt-3 w-full px-4 py-2 bg-rose-600 text-white text-sm font-700 disabled:opacity-40">
          Permanently delete my Health Vault
        </button>
      </div>
    </div>
  );
}
